import { DrawerCustom, Icon, Typography } from "@platform/design-system";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";

export interface CorrespondenceDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The RFP the drawer is scoped to, shown under the title. */
  subtitle?: string;
  /**
   * Widened state. The design draws the drawer at 800px and its thread list
   * at 1113px; the expand control is what moves between them.
   */
  expanded: boolean;
  onToggleExpanded: () => void;
  onMinimize: () => void;
  children: React.ReactNode;
}

const COLLAPSED_WIDTH = 800;
const EXPANDED_WIDTH = 1113;

/**
 * The shell every correspondence view sits in.
 *
 * A drawer rather than a workbench tab, which is how the design draws it: the
 * header carries minimize, expand and close, and an analyst reading a carrier
 * reply is usually doing it against the RFP page behind the drawer rather
 * than instead of it.
 *
 * Minimize is the caller's to interpret. This reports the intent and does not
 * animate anything itself, because where a minimized drawer goes depends on
 * the page hosting it.
 */
export const CorrespondenceDrawer = ({
  open,
  onClose,
  subtitle,
  expanded,
  onToggleExpanded,
  onMinimize,
  children,
}: CorrespondenceDrawerProps): React.JSX.Element => (
  <DrawerCustom
    anchor="right"
    open={open}
    onClose={onClose}
    data-testid="correspondence-drawer"
    sx={{
      "& .MuiDrawer-paper": {
        width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        maxWidth: "100vw",
        transition: "width 200ms",
      },
    }}
  >
    <Stack sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="header-small" component="h2">
            Communications
          </Typography>
          {subtitle && (
            <Typography
              data-testid="correspondence-drawer-subtitle"
              variant="caption"
              color="textSecondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <Tooltip title="Minimize">
            <IconButton
              aria-label="Minimize communications"
              onClick={onMinimize}
            >
              <Icon name="collapse" size="sm" />
            </IconButton>
          </Tooltip>
          <Tooltip title={expanded ? "Shrink" : "Expand"}>
            <IconButton
              aria-label={
                expanded ? "Shrink communications" : "Expand communications"
              }
              aria-pressed={expanded}
              onClick={onToggleExpanded}
            >
              <Icon name={expanded ? "collapse" : "expand"} size="sm" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton aria-label="Close communications" onClick={onClose}>
              <Icon name="close" size="sm" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>{children}</Box>
    </Stack>
  </DrawerCustom>
);
